/**
 * R100 · Shared column builder for the Tasks list.
 *
 * Both the main Tasks page and Projects > Tasks sub-tab render an
 * `AdvancedTable` with the SAME column shape so PMs see the same fields
 * wherever they land. Extracted so we don't drift.
 *
 * Callers must supply:
 *   { members, patchTask, canManageTask, memberName, InlineDateCell,
 *     isOverdue, PRIORITIES, STATUSES, fmtRelative, PersonChip,
 *     TaskTitleCell }
 *
 * `visible: false` columns are hidden by default but user-toggleable via
 * the "Columns" menu on the table.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import SearchableAssigneePicker from "@/components/tasks/SearchableAssigneePicker";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Repeat, CalendarDays } from "lucide-react";
import PriorityIcon from "@/components/tasks/PriorityIcon";
import PhasePicker from "@/components/tasks/PhasePicker";
import TaskCommentCell from "@/components/tasks/TaskCommentCell";

// Feb 2026 R293s · Currency symbol lookup (kept local to avoid a cycle
// with TaskFinanceDialog). Keep in sync with CURRENCIES there.
const CCY_SYMBOLS = { INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "AED" };

// Feb 2026 R293s · Inline money cell editor used by Est amount / Paid
// amount columns in the Tasks grid. Commits on blur / Enter; Escape
// restores. Keeps the keystrokes LOCAL so React doesn't re-render the
// whole row (and the filter chip bar) between characters.
function InlineTaskMoneyInput({ value, currency, onCommit, testid }) {
  const [v, setV] = useState(value == null ? "" : String(value));
  useEffect(() => { setV(value == null ? "" : String(value)); }, [value]);
  const sym = CCY_SYMBOLS[currency || "INR"] || currency || "₹";
  // Feb 2026 R293s · Escape must revert, never commit. setV(original)+blur()
  // runs commit() with the STALE closure v (the typed value) and would PATCH
  // it. This ref lets commit skip once when the Esc path triggered the blur.
  const skipCommitRef = useRef(false);
  const commit = () => {
    if (skipCommitRef.current) { skipCommitRef.current = false; return; }
    const trimmed = v.trim();
    const parsed = trimmed === "" ? null : Number(trimmed);
    const current = value == null ? null : Number(value);
    if (parsed === current) return;
    if (parsed !== null && !Number.isFinite(parsed)) { setV(current == null ? "" : String(current)); return; }
    onCommit(parsed);
  };
  return (
    <span className="inline-flex items-center gap-1 w-full">
      <span className="text-[10px] text-muted-foreground font-mono shrink-0">{sym}</span>
      <input
        type="number"
        step="0.01"
        min="0"
        value={v}
        onChange={(e) => setV(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") {
            skipCommitRef.current = true;
            setV(value == null ? "" : String(value));
            e.currentTarget.blur();
          }
        }}
        className="h-6 w-full text-right text-[11px] font-mono tabular-nums px-1 border border-transparent hover:border-border focus:border-primary rounded-none bg-background focus:outline-none"
        placeholder="0"
        data-testid={testid}
      />
    </span>
  );
}


// R158j · Feb 2026 — Human-friendly summary of a task's recurrence rule.
// Returns null if the task is not recurring.
function recurrenceSummary(t) {
  const rec = t?.recurrence;
  if (!rec || typeof rec !== "object") return null;
  const freq = rec.frequency || "weekly";
  const iv = rec.interval || 1;
  const wd = rec.weekdays || [];
  if (freq === "daily") return iv === 1 ? "Every day" : `Every ${iv} days`;
  if (freq === "weekly") {
    const base = iv === 1 ? "Every week" : `Every ${iv} weeks`;
    return wd.length ? `${base} · ${wd.join(", ")}` : base;
  }
  if (freq === "monthly") return iv === 1 ? "Every month" : `Every ${iv} months`;
  if (freq === "yearly") return iv === 1 ? "Every year" : `Every ${iv} years`;
  if (freq === "custom") return `Every ${iv} day${iv > 1 ? "s" : ""} (custom)`;
  return "Recurring";
}

// R127 · Feb 2026 — Inline feedback cell. Click to edit; blur / Enter
// saves; Escape reverts. Read-only when the current user can't edit
// this task (permission check delegated to the caller via canEdit).
function TaskFeedbackCell({ task, patchTask, canEdit }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(task.feedback || "");
  useEffect(() => { setValue(task.feedback || ""); }, [task.feedback]);
  if (editing) {
    return (
      <input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => {
          setEditing(false);
          const next = value.trim();
          if (next !== (task.feedback || "")) patchTask(task.id, { feedback: next || null });
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") { setValue(task.feedback || ""); setEditing(false); }
          if (e.key === "Enter") e.currentTarget.blur();
        }}
        placeholder="Add feedback…"
        className="w-full text-xs px-1.5 py-1 border border-primary bg-background rounded-none focus:outline-none"
        data-testid={`task-feedback-input-${task.id}`}
      />
    );
  }
  const shown = task.feedback || "";
  return (
    <button
      type="button"
      disabled={!canEdit}
      onClick={(e) => { e.stopPropagation(); if (canEdit) setEditing(true); }}
      className={`w-full text-left text-xs ${canEdit ? "cursor-text hover:text-primary" : "cursor-default"} truncate`}
      title={shown || (canEdit ? "Click to add feedback" : "No feedback")}
      data-testid={`task-feedback-cell-${task.id}`}
    >
      {shown ? shown : <span className="text-muted-foreground italic">{canEdit ? "Add feedback…" : "—"}</span>}
    </button>
  );
}

export function buildTaskColumns({
  members,
  patchTask,
  canManageTask,
  memberName,
  InlineDateCell,
  isOverdue,
  PRIORITIES,
  STATUSES,
  fmtRelative,
  PersonChip,
  TaskTitleCell,
  onOpenTask,
  // Feb 2026 R293h · Optional live-project resolver. When provided
  // (Project → Tasks subtab passes the loaded workspace projects list),
  // the Project column prefers the live project.name lookup over the
  // task's denormalised `project_name` field — so a renamed project or
  // a cross-project move never surfaces a stale label.
  projectsById = null,
  // Iter Feb2026 · Optional reorder handler. When provided (only on
  // Project → Tasks tab with phases grouped), a leading column renders
  // compact ↑ ↓ buttons that call this handler with the direction.
  onReorderTask = null,
  // Feb 2026 · When true (user has `content_calendar.view`), the
  // Tasks list exposes two extra columns — Deliverable name + Topic —
  // sourced from the linked content_item. Hidden by default on the
  // main Tasks page so columns stay compact; users enable via the
  // column selector. Project/CC-contextual tabs can flip `visible` to
  // true via their own column overrides.
  canSeeCC = false,
  // Feb 2026 · Called after a comment is successfully posted inline
  // from the Latest comment cell. The parent typically triggers a
  // list refresh so the denormalised `latest_comment_*` fields the
  // backend writes land in view.
  onCommentPosted = null,
}) {
  const cols = [
    ...(onReorderTask ? [{
      key: "_reorder", label: "", width: 56, visible: true, resizable: false, exportable: false,
      render: (t) => (
        <div className="inline-flex items-center gap-0.5" data-testid={`task-reorder-${t.id}`}>
          {/* Iter Feb2026 · Drag handle — the whole row is draggable
              via AdvancedTable's onRowDrop wiring, so this icon acts as
              a visual affordance. The ▲ ▼ buttons remain as a keyboard/
              touch fallback for non-drag reordering. */}
          <span
            className="w-4 h-5 text-muted-foreground/60 cursor-grab active:cursor-grabbing hidden sm:inline-flex items-center justify-center"
            title="Drag to reorder inside this phase"
            data-testid={`task-drag-handle-${t.id}`}>
            <span className="text-[12px] leading-none select-none">⋮⋮</span>
          </span>
          <button
            type="button"
            className="w-5 h-5 border border-border hover:bg-muted flex items-center justify-center text-muted-foreground"
            onClick={(e) => { e.stopPropagation(); onReorderTask(t, "up"); }}
            title="Move up in this phase"
            data-testid={`task-reorder-up-${t.id}`}>
            <span className="text-[10px] leading-none">▲</span>
          </button>
          <button
            type="button"
            className="w-5 h-5 border border-border hover:bg-muted flex items-center justify-center text-muted-foreground"
            onClick={(e) => { e.stopPropagation(); onReorderTask(t, "down"); }}
            title="Move down in this phase"
            data-testid={`task-reorder-down-${t.id}`}>
            <span className="text-[10px] leading-none">▼</span>
          </button>
        </div>
      ),
    }] : []),
    {
      // Feb 2026 · Task names can grow long; wrap onto multiple lines
      // instead of stretching the column. `maxWidth` caps growth in both
      // compact + comfortable modes.
      key: "title", label: "Task", width: 240, maxWidth: 240, wrap: true,
      sortable: true, filter: true, resizable: true,
      accessor: (t) => t.title,
      render: (t) => <TaskTitleCell task={t} onRename={(v) => patchTask(t.id, { title: v })} memberName={memberName} onOpenSubtask={onOpenTask} />,
    },
    {
      key: "tags", label: "Tags", width: 140, sortable: true, groupable: false, filterType: "text",
      accessor: (t) => (t.tags || []).join(", "),
      render: (t) => (
        (t.tags || []).length > 0 ? (
          <div className="flex flex-wrap gap-0.5" data-testid={`task-tags-cell-${t.id}`}>
            {(t.tags || []).map((tg) => (
              <span key={tg} className="chip chip-pending text-[9px]"
                    data-testid={`task-tag-${t.id}-${tg}`}>
                {tg}
              </span>
            ))}
          </div>
        ) : <span className="text-muted-foreground">—</span>
      ),
    },
    {
      key: "client_name", label: "Client", width: 160, sortable: true, groupable: true, filterType: "text",
      accessor: (t) => t.client_name || "",
      render: (t) => t.client_name
        ? <span className="text-xs truncate" data-testid={`task-client-${t.id}`}>{t.client_name}</span>
        : <span className="text-muted-foreground">—</span>,
    },
    {
      key: "brand_name", label: "Brand", width: 150, sortable: true, groupable: true, filterType: "text",
      accessor: (t) => t.brand_name || "",
      render: (t) => t.brand_name
        ? <span className="text-xs truncate" data-testid={`task-brand-${t.id}`}>{t.brand_name}</span>
        : <span className="text-muted-foreground">—</span>,
    },
    {
      key: "project_name", label: "Project", width: 170, sortable: true, groupable: true, filterType: "text",
      accessor: (t) => {
        if (projectsById && t.project_id) {
          const live = projectsById[t.project_id];
          if (live?.name) return live.name;
        }
        return t.project_name || "";
      },
      render: (t) => {
        let label = t.project_name || "";
        if (projectsById && t.project_id) {
          const live = projectsById[t.project_id];
          if (live?.name) label = live.name;
        }
        return label
          ? <span className="text-xs truncate" data-testid={`task-project-${t.id}`}>{label}</span>
          : <span className="text-muted-foreground">—</span>;
      },
    },
    // Feb 2026 · Content-calendar context columns, gated by the
    // `content_calendar.view` permission on the parent component.
    // Omitted entirely for users without the perm so the column
    // selector never surfaces them.
    ...(canSeeCC ? [
      {
        key: "cc_deliverable_name", label: "Deliverable", width: 220,
        sortable: true, groupable: true, filterType: "text", visible: false,
        accessor: (t) => t.cc_deliverable_name || "",
        render: (t) => t.cc_deliverable_name
          ? (
            <span className="inline-flex items-center gap-1 min-w-0" data-testid={`task-cc-deliverable-wrap-${t.id}`}>
              <span className="text-xs truncate" title={t.cc_deliverable_name}
                     data-testid={`task-cc-deliverable-${t.id}`}>{t.cc_deliverable_name}</span>
              {t.content_item_id && (
                <Link
                  to={`/app/content-calendar?open=${encodeURIComponent(t.content_item_id)}`}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-0.5 shrink-0 text-[9px] uppercase tracking-widest font-semibold text-primary hover:underline border border-primary/30 hover:border-primary px-1 py-0.5 bg-primary/5"
                  title="Open the parent deliverable in the Content Calendar"
                  data-testid={`task-cc-open-${t.id}`}
                >
                  <CalendarDays className="w-2.5 h-2.5" /> Open
                </Link>
              )}
            </span>
          )
          : <span className="text-muted-foreground">—</span>,
      },
      {
        key: "cc_topic", label: "Topic", width: 180,
        sortable: true, groupable: true, filterType: "text", visible: false,
        accessor: (t) => t.cc_topic || "",
        render: (t) => t.cc_topic
          ? <span className={`text-xs truncate ${t.cc_topic_linked ? "font-bold text-emerald-800" : "font-semibold italic text-muted-foreground"}`}
                   title={`Topic: ${t.cc_topic}`}
                   data-testid={`task-cc-topic-${t.id}`}>{t.cc_topic}</span>
          : <span className="text-muted-foreground">—</span>,
      },
    ] : []),
    // Iter Feb2026 · Project phase — hidden by default on the main
    // Tasks page (where the column is only meaningful when scoped to a
    // project). ProjectTasksSubTab flips `visible: true` via its own
    // column override. Groupable so users can slice by phase. Inline
    // popover picker so PMs can change the phase without opening the
    // detail dialog.
    {
      key: "phase_name", label: "Phase", width: 150, sortable: true, groupable: true, filterType: "text", visible: false,
      accessor: (t) => t.phase_name || "Unphased",
      render: (t) => (
        <PhasePicker
          projectId={t.project_id}
          value={t.phase_id}
          fallbackLabel={t.phase_name}
          onChange={(next) => patchTask(t.id, { phase_id: next })}
          testid={`task-phase-picker-${t.id}`}
          disabled={!canManageTask(t)}
        />
      ),
    },
    // R268llll · Feb 2026 — Department (derived on Tasks page from
    // the task's project). Hidden by default so it doesn't take table
    // real-estate but appears in Group by and the column-visibility
    // menu.
    {
      key: "department_name", label: "Department", width: 160, sortable: true, groupable: true, filterType: "text", visible: false,
      accessor: (t) => t.department_name || "",
      render: (t) => t.department_name
        ? <span className="text-xs truncate" data-testid={`task-department-${t.id}`}>{t.department_name}</span>
        : <span className="text-muted-foreground">—</span>,
    },
    // ── R127 · Feb 2026 — Delivery-perf columns (middle-left placement)
    // Delivered in days · Delayed by · Rating · Feedback
    {
      key: "delivered_in_days", label: "Delivered in days", width: 130, sortable: true, filterType: "number",
      accessor: (t) => {
        if (t.status !== "done" || !t.actual_end_date || !t.created_at) return null;
        const ms = new Date(t.actual_end_date).getTime() - new Date(t.created_at).getTime();
        return Math.round(ms / 86400000);
      },
      render: (t) => {
        if (t.status !== "done" || !t.actual_end_date || !t.created_at) {
          return <span className="text-muted-foreground text-xs">—</span>;
        }
        const ms = new Date(t.actual_end_date).getTime() - new Date(t.created_at).getTime();
        const d = Math.round(ms / 86400000);
        return (
          <span className="font-mono text-xs" data-testid={`task-delivered-in-${t.id}`}>
            {d}{d === 1 ? " day" : " days"}
          </span>
        );
      },
    },
    {
      key: "delayed_by", label: "Delayed by", width: 130, sortable: true, filterType: "number",
      accessor: (t) => {
        if (!t.due_date) return null;
        // Done → compare actual end vs due.
        if (t.status === "done" && t.actual_end_date) {
          return Math.round(
            (new Date(t.actual_end_date).getTime() - new Date(t.due_date).getTime()) / 86400000
          );
        }
        // Open + past-due → live overdue count.
        if (t.status !== "done") {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const due = new Date(t.due_date);
          due.setHours(0, 0, 0, 0);
          const diff = Math.round((today.getTime() - due.getTime()) / 86400000);
          return diff > 0 ? diff : 0;
        }
        return null;
      },
    render: (t) => {
        if (!t.due_date) return <span className="text-muted-foreground text-xs">—</span>;
        let days;
        let finalized = false;
        if (t.status === "done" && t.actual_end_date) {
          days = Math.round(
            (new Date(t.actual_end_date).getTime() - new Date(t.due_date).getTime()) / 86400000
          );
          finalized = true;
        } else if (t.status !== "done") {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const due = new Date(t.due_date);
          due.setHours(0, 0, 0, 0);
          days = Math.round((today.getTime() - due.getTime()) / 86400000);
        } else {
          return <span className="text-muted-foreground text-xs">—</span>;
        }
        const label = days < 0
          ? (finalized ? `${Math.abs(days)}d early` : `${Math.abs(days)}d to go`)
          : days === 0
          ? "On time"
          : `${days}d late`;
        // R148g · Feb 2026 — "days to go" on live open tasks reads more
        // intuitively than "early" (which implies delivery already
        // happened). Reserve "early" for finalized (done) tasks.
        const cls = days < 0
          ? (finalized ? "text-emerald-600 bg-emerald-50 border-emerald-300" : "text-sky-700 bg-sky-50 border-sky-300")
          : days === 0
          ? "text-emerald-700 bg-emerald-50 border-emerald-300"
          : "text-rose-600 bg-rose-50 border-rose-300";
        const suffix = finalized ? "" : (days > 0 ? " · open" : "");
        return (
          <span className={`text-[10px] px-1.5 py-0.5 border font-semibold ${cls}`}
                data-testid={`task-delayed-by-${t.id}`}
                title={finalized ? "Final (task completed)" : "Live (task still open)"}>
            {label}{suffix}
          </span>
        );
      },
    },
    {
      key: "rating", label: "Rating", width: 120, sortable: true, filterType: "number", stopPropagation: true,
      accessor: (t) => t.rating || 0,
      render: (t) => {
        const rating = t.rating || 0;
        const canEdit = canManageTask(t);
        return (
          <div className="flex items-center gap-0.5" data-testid={`task-rating-${t.id}`}>
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                disabled={!canEdit}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!canEdit) return;
                  const next = rating === n ? null : n;
                  patchTask(t.id, { rating: next });
                }}
                className={`text-base leading-none ${n <= rating ? "text-amber-500" : "text-muted-foreground/40"} ${canEdit ? "hover:text-amber-600 cursor-pointer" : "cursor-default"}`}
                title={canEdit ? `Rate ${n}` : "Read-only"}
                data-testid={`task-rating-star-${t.id}-${n}`}
              >★</button>
            ))}
          </div>
        );
      },
    },
    {
      key: "feedback", label: "Feedback", width: 220, sortable: true, filterType: "text", stopPropagation: true,
      accessor: (t) => t.feedback || "",
      render: (t) => <TaskFeedbackCell task={t} patchTask={patchTask} canEdit={canManageTask(t)} />,
    },
    {
      key: "priority", label: "Priority", width: 110, sortable: true, groupable: true, stopPropagation: true,
      filterType: "select", filterOptions: PRIORITIES.map((p) => ({ value: p, label: p })),
      accessor: (t) => t.priority,
      render: (t) => (
        // Feb 2026 R291i · Removed external PriorityIcon duplicate.
        // The SelectValue already renders the icon via the active
        // SelectItem content (see SelectItem children below), so the
        // outer div only hosts the Select.
        <Select value={t.priority} onValueChange={(v) => patchTask(t.id, { priority: v })}>
          <SelectTrigger className="h-7 text-xs rounded-none" data-testid={`task-priority-${t.id}`}>
            <SelectValue>
              <span className="inline-flex items-center gap-1.5">
                <PriorityIcon priority={t.priority || "medium"} className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate">{t.priority || "medium"}</span>
              </span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {PRIORITIES.map((p) => (
              <SelectItem key={p} value={p}>
                <span className="inline-flex items-center gap-1.5">
                  <PriorityIcon priority={p} className="w-3.5 h-3.5" />
                  {p}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ),
    },
    {
      key: "recurrence", label: "Recurrence", width: 160, sortable: true, groupable: true,
      accessor: (t) => recurrenceSummary(t) || "",
      render: (t) => {
        const s = recurrenceSummary(t);
        if (!s) return <span className="text-muted-foreground text-xs">—</span>;
        return (
          <span className="inline-flex items-center gap-1 text-xs" title={s} data-testid={`task-recurrence-${t.id}`}>
            <Repeat className="w-3 h-3 text-primary" />
            <span className="truncate">{s}</span>
          </span>
        );
      },
    },
    {
      key: "progress_percent", label: "%", width: 110, sortable: true, type: "number", align: "right",
      accessor: (t) => t.progress_percent ?? 0,
      render: (t) => (
        <div className="flex items-center gap-1 min-w-0" data-testid={`task-progress-${t.id}`}>
          <div className="flex-1 h-1.5 bg-muted min-w-[40px]">
            <div className="h-full bg-primary transition-all"
                  style={{ width: `${t.progress_percent || 0}%` }} />
          </div>
          <span className="text-[10px] font-mono w-8 text-right">{t.progress_percent || 0}%</span>
        </div>
      ),
    },
    {
      key: "status", label: "Status", width: 140, sortable: true, groupable: true, stopPropagation: true,
      filterType: "select", filterOptions: STATUSES.map((s) => ({ value: s, label: s.replace("_", " ") })),
      accessor: (t) => t.status,
      render: (t) => (
        <Select value={t.status} onValueChange={(v) => patchTask(t.id, { status: v })}>
          <SelectTrigger className="h-7 text-xs" data-testid={`task-status-${t.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
        </Select>
      ),
    },
    {
      key: "assignee", label: "Assignee", width: 160, sortable: true, groupable: true, filterType: "text",
      accessor: (t) => memberName(t.assignee_id),
      render: (t) => (
        <div onClick={(e) => e.stopPropagation()} className="w-full">
          <SearchableAssigneePicker
            value={t.assignee_id}
            members={members}
            memberName={memberName}
            onChange={(v) => patchTask(t.id, { assignee_id: v })}
            testid={`task-inline-assignee-${t.id}`}
          />
        </div>
      ),
    },
    {
      key: "health", label: "Health", width: 110, sortable: true, groupable: true, visible: false,
      filterType: "select", filterOptions: [
        { value: "on_track", label: "🟢 On track" },
        { value: "at_risk", label: "🟡 At risk" },
        { value: "off_track", label: "🔴 Off track" },
      ],
      accessor: (t) => t.health || "on_track",
      render: (t) => {
        const h = t.health || "on_track";
        const emoji = h === "off_track" ? "🔴" : h === "at_risk" ? "🟡" : "🟢";
        return <span className="text-xs">{emoji} {h.replace("_", " ")}</span>;
      },
    },
    {
      key: "start_date", label: "Planned start", width: 130, sortable: true, align: "right", filterType: "date-range", visible: false,
      accessor: (t) => t.start_date || "",
      render: (t) => (
        <InlineDateCell
          value={t.start_date}
          onChange={(iso) => patchTask(t.id, { start_date: iso || null })}
          canEdit={canManageTask(t)}
          testid={`task-inline-start-${t.id}`}
        />
      ),
    },
    {
      key: "due_date", label: "Planned end · Due", width: 150, sortable: true, align: "right", filterType: "date-range",
      accessor: (t) => t.due_date || "",
      render: (t) => (
        <InlineDateCell
          value={t.due_date}
          onChange={(iso) => patchTask(t.id, { due_date: iso || null })}
          canEdit={canManageTask(t)}
          testid={`task-inline-due-${t.id}`}
          overdue={isOverdue(t)}
        />
      ),
    },
    {
      key: "actual_start_date", label: "Actual start", width: 130, sortable: true, align: "right", filterType: "date-range", visible: false,
      accessor: (t) => t.actual_start_date || "",
      render: (t) => (
        <InlineDateCell
          value={t.actual_start_date}
          onChange={(iso) => patchTask(t.id, { actual_start_date: iso || null })}
          canEdit={canManageTask(t)}
          testid={`task-inline-actual-start-${t.id}`}
          mono
        />
      ),
    },
    {
      key: "actual_end_date", label: "Actual end", width: 130, sortable: true, align: "right", filterType: "date-range", visible: false,
      accessor: (t) => t.actual_end_date || "",
      render: (t) => (
        <InlineDateCell
          value={t.actual_end_date}
          onChange={(iso) => patchTask(t.id, { actual_end_date: iso || null })}
          canEdit={canManageTask(t)}
          testid={`task-inline-actual-end-${t.id}`}
          mono
        />
      ),
    },
    {
      key: "est_hours", label: "Est. hrs", width: 80, sortable: true, align: "right", type: "number", sum: true, visible: false,
      accessor: (t) => t.est_hours || 0,
      render: (t) => t.est_hours ? <span className="font-mono text-xs">{t.est_hours}</span> : <span className="text-muted-foreground">—</span>,
    },
    {
      key: "actual_hours", label: "Actual hrs", width: 80, sortable: true, align: "right", type: "number", sum: true, visible: false,
      accessor: (t) => t.actual_hours || 0,
      render: (t) => t.actual_hours ? <span className="font-mono text-xs">{t.actual_hours}</span> : <span className="text-muted-foreground">—</span>,
    },
    {
      key: "manual_effort_hours", label: "Manual effort (h)", width: 130, sortable: true, align: "right", type: "number", sum: true,
      accessor: (t) => t.manual_effort_hours || 0,
      render: (t) => t.manual_effort_hours ? (
        <span className="font-mono text-xs" title={t.manual_effort_note || ""}>{t.manual_effort_hours}{t.manual_effort_note ? " •" : ""}</span>
      ) : <span className="text-muted-foreground">—</span>,
    },
    {
      key: "timesheet_hours", label: "Timesheet (h)", width: 110, sortable: true, align: "right", type: "number", sum: true,
      accessor: (t) => t.timesheet_hours || 0,
      render: (t) => t.timesheet_hours ? <span className="font-mono text-xs text-primary">{t.timesheet_hours}</span> : <span className="text-muted-foreground">—</span>,
    },
    {
      key: "total_effort_hours", label: "Total effort (h)", width: 120, sortable: true, align: "right", type: "number", sum: true,
      accessor: (t) => t.total_effort_hours || 0,
      render: (t) => t.total_effort_hours ? <span className="font-mono text-xs font-semibold">{t.total_effort_hours}</span> : <span className="text-muted-foreground">—</span>,
    },
    // R148g · Feb 2026 — Budget delta column. Positive = under budget
    // (green, hours saved), negative = over budget (red, hours blown).
    // Uses `est_hours` as the budget baseline and the annotated
    // `total_effort_hours` roll-up as actual.
    {
      key: "budget_delta", label: "Budget", width: 130, sortable: true, align: "right", type: "number",
      accessor: (t) => {
        const est = Number(t.est_hours || 0);
        const act = Number(t.total_effort_hours || 0);
        return est > 0 ? est - act : 0;
      },
      render: (t) => {
        const est = Number(t.est_hours || 0);
        const act = Number(t.total_effort_hours || 0);
        if (!est) return <span className="text-muted-foreground text-[10px]" title="No estimate set">No est.</span>;
        if (!act) return <span className="text-muted-foreground text-[10px]" title={`Budget ${est}h · no hours logged yet`}>—</span>;
        const diff = est - act;
        const rounded = Math.abs(Math.round(diff * 10) / 10);
        if (diff >= 0) {
          return (
            <span
              className="inline-block border border-emerald-500 bg-emerald-50 text-emerald-700 px-1.5 text-[10px] font-mono font-bold"
              title={`Under budget by ${rounded}h · Estimated ${est}h · Logged ${act}h`}
              data-testid={`task-budget-under-${t.id}`}
            >
              −{rounded}h
            </span>
          );
        }
        return (
          <span
            className="inline-block border border-rose-500 bg-rose-50 text-rose-700 px-1.5 text-[10px] font-mono font-bold"
            title={`Over budget by ${rounded}h · Estimated ${est}h · Logged ${act}h`}
            data-testid={`task-budget-over-${t.id}`}
          >
            +{rounded}h
          </span>
        );
      },
    },
    {
      key: "billable", label: "Billable", width: 90, sortable: true, filterType: "boolean", visible: false,
      accessor: (t) => !!t.billable,
      render: (t) => (
        <div onClick={(e) => e.stopPropagation()} className="text-center">
          <input type="checkbox" checked={!!t.billable}
                  onChange={(e) => patchTask(t.id, { billable: e.target.checked })}
                  className="h-4 w-4 accent-primary cursor-pointer"
                  data-testid={`task-billable-${t.id}`} />
        </div>
      ),
    },
    {
      // Feb 2026 · "Ready bill" — the handoff state between day-to-day
      // work and the Billing bulk bar. Enabled only after the task is
      // marked Billable; disabled once the invoice has gone out. The
      // backend field is `ready_to_bill` (matches PATCH payload).
      key: "ready_to_bill", label: "Ready bill", width: 95, sortable: true, filterType: "boolean", visible: true,
      accessor: (t) => !!t.ready_to_bill,
      render: (t) => (
        <div onClick={(e) => e.stopPropagation()} className="text-center">
          <input
            type="checkbox"
            checked={!!t.ready_to_bill}
            disabled={!t.billable || !!t.billed}
            onChange={(e) => patchTask(t.id, { ready_to_bill: e.target.checked })}
            className="h-4 w-4 accent-amber-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
            title={!t.billable ? "Mark as billable first" : (t.billed ? "Already billed" : (t.ready_to_bill ? "Ready to invoice" : "Mark ready to invoice"))}
            data-testid={`task-ready-bill-${t.id}`}
          />
        </div>
      ),
    },
    {
      key: "billed", label: "Billed", width: 80, sortable: true, filterType: "boolean", visible: false,
      accessor: (t) => !!t.billed,
      render: (t) => (
        <div onClick={(e) => e.stopPropagation()} className="text-center">
          <input type="checkbox" checked={!!t.billed} disabled={!t.billable}
                  onChange={(e) => patchTask(t.id, { billed: e.target.checked })}
                  className="h-4 w-4 accent-emerald-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                  title={!t.billable ? "Mark as billable first" : ""}
                  data-testid={`task-billed-${t.id}`} />
        </div>
      ),
    },
    {
      key: "paid", label: "Paid", width: 80, sortable: true, filterType: "boolean", visible: false,
      accessor: (t) => !!t.paid,
      render: (t) => (
        <div onClick={(e) => e.stopPropagation()} className="text-center">
          <input type="checkbox" checked={!!t.paid} disabled={!t.billed}
                  onChange={(e) => patchTask(t.id, { paid: e.target.checked })}
                  className="h-4 w-4 accent-emerald-700 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                  title={!t.billed ? "Mark as billed first" : ""}
                  data-testid={`task-paid-${t.id}`} />
        </div>
      ),
    },
    {
      // Feb 2026 · Date stamps for the Billed / Paid lifecycle. Shown
      // as sortable date columns; also editable inline from the Edit
      // Task popup's billing section. Auto-filled by the backend when
      // the admin flips the flag without picking a date.
      key: "billed_date", label: "Billed date", width: 110, sortable: true, filterType: "date", visible: false,
      accessor: (t) => t.billed_date || "",
      render: (t) => (
        <span className="text-xs tabular-nums text-muted-foreground" data-testid={`task-billed-date-${t.id}`}>
          {t.billed_date || "—"}
        </span>
      ),
    },
    {
      key: "paid_date", label: "Paid date", width: 110, sortable: true, filterType: "date", visible: false,
      accessor: (t) => t.paid_date || "",
      render: (t) => (
        <span className="text-xs tabular-nums text-muted-foreground" data-testid={`task-paid-date-${t.id}`}>
          {t.paid_date || "—"}
        </span>
      ),
    },
    {
      // Feb 2026 · Billing month (YYYY-MM) — accounting bucket for
      // this task's billed amount. Auto-filled from Billed date on
      // first stamp but overridable from the Edit Task popup.
      // Feb 2026 R293s · Now inline-editable so a PM can backdate
      // the accounting month straight from the row.
      key: "billing_month", label: "Billing month", width: 130, sortable: true, filterType: "text", visible: false, stopPropagation: true,
      accessor: (t) => t.billing_month || "",
      render: (t) => (
        <div onClick={(e) => e.stopPropagation()}>
          <input
            type="month"
            value={t.billing_month || ""}
            onChange={(e) => patchTask(t.id, { billing_month: e.target.value || null })}
            className="h-6 text-[11px] px-1 border border-transparent hover:border-border focus:border-primary rounded-none bg-background tabular-nums"
            data-testid={`task-billing-month-${t.id}`}
            title="Accounting month this bill lands in"
          />
        </div>
      ),
    },
    {
      // Feb 2026 R293s · Currency (per-task). Inline select so Finance
      // can flip a row's currency without opening the Finance dialog.
      key: "currency", label: "Currency", width: 90, sortable: true, filterType: "select", visible: false, stopPropagation: true,
      filterOptions: ["INR","USD","EUR","GBP","AED"].map((c) => ({ value: c, label: c })),
      accessor: (t) => t.currency || "INR",
      render: (t) => (
        <div onClick={(e) => e.stopPropagation()} className="text-center">
          <select
            value={t.currency || "INR"}
            onChange={(e) => patchTask(t.id, { currency: e.target.value })}
            className="h-6 text-[11px] px-1 border border-transparent hover:border-border focus:border-primary rounded-none bg-background font-mono"
            data-testid={`task-currency-${t.id}`}
          >
            {["INR","USD","EUR","GBP","AED"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      ),
    },
    {
      // Feb 2026 R293s · Estimated billable amount (per-task money).
      key: "est_amount", label: "Est. amount", width: 110, sortable: true, filterType: "number-range", visible: false, type: "currency", sum: true, stopPropagation: true,
      accessor: (t) => Number(t.est_amount || 0),
      exportValue: (t) => Number(t.est_amount || 0),
      render: (t) => (
        <div onClick={(e) => e.stopPropagation()} className="text-right">
          <InlineTaskMoneyInput
            value={t.est_amount}
            currency={t.currency || "INR"}
            onCommit={(v) => patchTask(t.id, { est_amount: v })}
            testid={`task-est-amt-${t.id}`}
          />
        </div>
      ),
    },
    {
      // Feb 2026 R293s · Paid amount (per-task money).
      key: "paid_amount", label: "Paid amount", width: 110, sortable: true, filterType: "number-range", visible: false, type: "currency", sum: true, stopPropagation: true,
      accessor: (t) => Number(t.paid_amount || 0),
      exportValue: (t) => Number(t.paid_amount || 0),
      render: (t) => (
        <div onClick={(e) => e.stopPropagation()} className="text-right">
          <InlineTaskMoneyInput
            value={t.paid_amount}
            currency={t.currency || "INR"}
            onCommit={(v) => patchTask(t.id, { paid_amount: v })}
            testid={`task-paid-amt-${t.id}`}
          />
        </div>
      ),
    },
    {
      // Feb 2026 R293s · Balance (Est − Paid). Read-only; coloured so a
      // positive balance stands out as outstanding.
      key: "balance_amount", label: "Balance", width: 110, sortable: true, visible: false, type: "currency", sum: true,
      accessor: (t) => Number(t.est_amount || 0) - Number(t.paid_amount || 0),
      exportValue: (t) => Number(t.est_amount || 0) - Number(t.paid_amount || 0),
      render: (t) => {
        const est = Number(t.est_amount || 0);
        const paid = Number(t.paid_amount || 0);
        const bal = est - paid;
        const sym = CCY_SYMBOLS[t.currency || "INR"] || t.currency || "₹";
        const cls = bal > 0 ? "text-rose-700 font-semibold" : bal < 0 ? "text-emerald-700 font-semibold" : "text-muted-foreground";
        return (
          <span className={`text-[11px] font-mono tabular-nums ${cls}`} data-testid={`task-balance-${t.id}`}>
            {est === 0 && paid === 0 ? "—" : `${sym} ${new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(bal)}`}
          </span>
        );
      },
    },
    {
      // Feb 2026 R292w · Task category — admin-managed label pulled
      // from the denormalised `task_category_name` so the cell renders
      // without an extra lookup. Hidden by default to keep the list
      // compact; users switch it on from the Columns menu.
      key: "task_category_name", label: "Category", width: 140, sortable: true, filterType: "text", visible: false,
      accessor: (t) => t.task_category_name || "",
      render: (t) => (
        t.task_category_name
          ? <span className="inline-flex items-center text-[11px] px-1.5 py-0.5 border border-border bg-muted/40 font-medium"
                  data-testid={`task-category-${t.id}`}>{t.task_category_name}</span>
          : <span className="text-muted-foreground text-xs">—</span>
      ),
    },
    {
      // Feb 2026 · Latest comment is now clickable — opens a tiny
      // popover with a textarea and Internal/Client toggle, mirroring
      // the Feedback cell behaviour the user asked about. Default
      // visible=true so SDRs/PMs discover the inline post flow.
      key: "latest_comment_body", label: "Latest comment", width: 240, sortable: true, filterType: "text", visible: true, stopPropagation: true,
      accessor: (t) => t.latest_comment_body || "",
      render: (t) => (
        <TaskCommentCell
          task={t}
          canEdit={canManageTask ? canManageTask(t) : true}
          fmtRelative={fmtRelative}
          onPosted={(doc) => onCommentPosted?.(t, doc)}
        />
      ),
    },
    {
      key: "latest_comment_at", label: "Latest comment date", width: 150, sortable: true, filterType: "date", visible: false,
      accessor: (t) => t.latest_comment_at || "",
      render: (t) => t.latest_comment_at ? (
        <span className="text-xs text-muted-foreground" title={t.latest_comment_at}>{fmtRelative(t.latest_comment_at)}</span>
      ) : <span className="text-muted-foreground">—</span>,
    },
    {
      key: "updated_by_name", label: "Updated by", width: 140, sortable: true, filterType: "text", visible: false,
      accessor: (t) => t.updated_by_name || "",
      render: (t) => (
        <div className="min-w-0">
          <div className="text-xs truncate">{t.updated_by_name || "—"}</div>
          <div className="text-[10px] text-muted-foreground">{t.updated_at ? fmtRelative(t.updated_at) : ""}</div>
        </div>
      ),
    },
    {
      key: "age", label: "Age", width: 90, sortable: true, align: "right",
      accessor: (t) => t.created_at,
      render: (t) => <span className="text-xs text-muted-foreground">{fmtRelative(t.created_at)}</span>,
    },
  ];
  // Feb 2026 R291c · Reorder the default visible columns per user
  // feedback so the first screen shows the information that matters
  // most on a glance:
  //   drag · title · assignee · status · priority · due date · project
  // Everything else keeps its original relative order. Columns are
  // sorted by (preferredIndex, originalIndex) so columns NOT in the
  // preferred list fall through in their declared order.
  const PREFERRED_ORDER = [
    "_reorder", "title", "assignee", "status", "priority", "due_date", "project_name",
  ];
  const originalOrder = new Map(cols.map((c, i) => [c.key, i]));
  cols.sort((a, b) => {
    const ai = PREFERRED_ORDER.indexOf(a.key);
    const bi = PREFERRED_ORDER.indexOf(b.key);
    const aw = ai === -1 ? 1000 + originalOrder.get(a.key) : ai;
    const bw = bi === -1 ? 1000 + originalOrder.get(b.key) : bi;
    return aw - bw;
  });
  return cols;
}
